// src/app/services/theme.service.ts
import { Injectable } from '@angular/core';
import themeData from '../../styles/material-theme.json';

type SchemeNames = 'light' | 'dark' | 'light-medium-contrast' | 'light-high-contrast' | 'dark-medium-contrast' | 'dark-high-contrast';

type SchemeColors = {
  [key: string]: string;
};

type ThemeData = {
  description: string;
  seed: string;
  coreColors: {
    primary: string;
    secondary: string;
    tertiary: string;
    error: string;
  };
  extendedColors: never[];
  schemes: {
    [K in SchemeNames]: SchemeColors;
  };
  palettes: {
    [key: string]: {
      [key: string]: string;
    };
  };
};

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentScheme: SchemeNames = 'light-medium-contrast';

  constructor() {
    // First apply all theme variables
    this.applyTheme();

    this.setScheme(this.currentScheme);
    // Then detect and set the system theme
    //this.detectSystemTheme();
    
    // Optionally: Add listener for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', e => {
        this.setScheme(e.matches ? 'dark' : 'light');
      });
  }

  applyTheme() {
    const root = document.documentElement;
    const theme = themeData as ThemeData;

    const processTheme = (obj: any, prefix: string = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        
        if (typeof value === 'string') {
          const varName = `--${prefix}${key}`;
          root.style.setProperty(varName, value);
        } else if (value && typeof value === 'object') {
          // Recursively process nested objects
          processTheme(value, `${prefix}${key}-`);
        }
      });
    };

    processTheme(theme);
  }

  setScheme(schemeName: SchemeNames) {
    this.currentScheme = schemeName;
    const schemeColors = (themeData as ThemeData).schemes[schemeName];
    
    if (schemeColors) {
      // Update CSS variables
      Object.entries(schemeColors).forEach(([key, value]) => {
          const varName = `--${key}`;
          document.documentElement.style.setProperty(varName, value);
      });

      // Update theme-color meta tag
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (themeColorMeta) {
          themeColorMeta.setAttribute('content', schemeColors['primary']);
      }

      // Update Material theme classes
      const body = document.body;
      // Remove all existing theme classes
      body.classList.remove('light-theme', 'dark-theme', 'light-medium-contrast-theme', 'light-high-contrast-theme', 'dark-medium-contrast-theme', 'dark-high-contrast-theme');
      // Add new theme class
      body.classList.add(`${schemeName}-theme`);
    }
  }

  // Add method to detect system theme
  detectSystemTheme() {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.setScheme(isDark ? 'dark' : 'light');
  }
}
